/**
 * Pure parsing + shaping helpers for the `find_entity_by_name` tool result. No
 * React, no IO — the controller (FindEntityByNameToolUI) and the presentational
 * view both consume these.
 */

export interface FindEntityByNameArgs {
  query: string
  limit?: number
}

export interface EntityCandidate {
  element_id: string
  name: string
  labels: string[]
  properties?: Record<string, unknown>
}

export interface FindEntityByNameResult {
  status: 'ok' | 'empty' | 'error'
  query: string
  count?: number
  candidates?: EntityCandidate[]
  error?: string
}

const INTERNAL_LABEL_PREFIX = '__'

export function primaryLabel(labels: string[]): string | undefined {
  return labels.find(l => !l.startsWith(INTERNAL_LABEL_PREFIX))
}

export function visibleLabels(labels: string[]): string[] {
  return labels.filter(l => !l.startsWith(INTERNAL_LABEL_PREFIX))
}

export function parseResult(raw: unknown): FindEntityByNameResult | null {
  if (raw == null) return null
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw) as FindEntityByNameResult
    } catch {
      return { status: 'error', query: '', error: raw }
    }
  }
  return raw as FindEntityByNameResult
}

/** Read a candidate's coordinates, accepting either naming convention. */
export function entityCoords(props?: Record<string, unknown>): { lat: number; lon: number } | null {
  if (!props) return null
  const lat = Number(props.lat ?? props.latitude)
  const lon = Number(props.lon ?? props.longitude)
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null
  return { lat, lon }
}

const PROPERTY_ORDER = ['iban', 'org_no', 'vat_id', 'country', 'tx_id', 'address']

export function compactProperties(props?: Record<string, unknown>): string[] {
  if (!props) return []
  const out: string[] = []
  for (const key of PROPERTY_ORDER) {
    const v = props[key]
    if (v == null || v === '') continue
    out.push(`${key}: ${String(v)}`)
  }
  return out
}
